/**
 * pastoralCadenceVariation.js — FASE R6 anti-formula system.
 *
 * Reads the last 4 assistant messages in conversation history,
 * extracts structural "shapes" (opener type, closer type, length band),
 * and detects if the same pattern is repeating.
 *
 * Returns variation hints to prevent templated responses.
 * Does NOT use external state — reads from historyMsgs directly.
 */

import { OPENER_PATTERNS, CLOSER_PATTERNS } from "./humanTextureLibrary";

/**
 * Classify text against a pattern map.
 * Returns the first matching type key, or the fallback.
 */
function classifyLine(text, patterns) {
  for (const [type, regexes] of Object.entries(patterns)) {
    if (regexes.length > 0 && regexes.some((r) => r.test(text))) return type;
  }
  return "statement";  // fallback for both openers and closers
}

/**
 * Extract the structural "shape" of a response.
 */
function extractShape(text) {
  const lines     = text.trim().split(/\n/).filter(Boolean);
  const firstLine = lines[0] ?? "";
  const lastLine  = lines[lines.length - 1] ?? "";
  const words     = text.trim().split(/\s+/).filter(Boolean).length;

  return {
    opener:    classifyLine(firstLine, OPENER_PATTERNS),
    closer:    classifyLine(lastLine,  CLOSER_PATTERNS),
    length:    words < 35 ? "short" : words < 90 ? "medium" : "long",
    hasList:   /^\s*[-\d•]\s/m.test(text),
  };
}

/**
 * Detect structural repetition in recent assistant responses.
 *
 * @param {Array<{ role: string, content?: string }>} historyMsgs
 * @returns {{
 *   variationNeeded: boolean,
 *   avoidPatterns:   string[],
 *   recentShapes:    object[],
 * }}
 */
export function detectCadenceRepetition(historyMsgs = []) {
  const assistantMsgs = historyMsgs
    .filter((m) => m.role === "assistant" && m.content)
    .slice(-4);  // analyze last 4 responses at most

  if (assistantMsgs.length < 2) {
    return { variationNeeded: false, avoidPatterns: [], recentShapes: [] };
  }

  const shapes     = assistantMsgs.map((m) => extractShape(m.content));
  const avoidPatterns = [];

  const last3Openers = shapes.slice(-3).map((s) => s.opener);
  const last3Closers = shapes.slice(-3).map((s) => s.closer);
  const last3Lengths = shapes.slice(-3).map((s) => s.length);

  // Same opener type 3 times in a row → avoid it
  if (last3Openers.length >= 3 && last3Openers.every((o) => o === last3Openers[0])) {
    avoidPatterns.push(`opener:${last3Openers[0]}`);
  }

  // Same closer type 3 times in a row → vary the ending
  if (last3Closers.length >= 3 && last3Closers.every((c) => c === last3Closers[0])) {
    avoidPatterns.push(`closer:${last3Closers[0]}`);
  }

  // Same length band 3 times in a row → change density
  if (last3Lengths.length >= 3 && last3Lengths.every((l) => l === last3Lengths[0])) {
    avoidPatterns.push(`length:${last3Lengths[0]}`);
  }

  // List used in last 2 responses → avoid list again
  const last2     = shapes.slice(-2);
  const bothLists = last2.length === 2 && last2.every((s) => s.hasList);
  if (bothLists) avoidPatterns.push("structure:list");

  return {
    variationNeeded: avoidPatterns.length > 0,
    avoidPatterns,
    recentShapes: shapes,
  };
}
